// The issues grid's data — IA_ABSORPTION_PLAN Phase 4 step 13.3a. `@rdrive/plugin-items-grid`'s
// IssuesGrid (an app component, `components/grids/IssuesGrid.vue`, until 2026-10-12) renders
// `@rdrive/plugin-data-table`'s DuckDbDataTable (paging, sort, debounced search, the filter-context
// reloads) and binds what this returns, through `useIssuesGrid.js`; neither holds SQL, so
// every query, the where-clauses and the row shape are here. They are `IssuesGrid.vue`'s own query
// builder of HEAD (380e962), verbatim apart from escaping: the search term and the page's ids go
// through `escapeSql` (the same text unless they hold an apostrophe — which used to break both
// queries: `Parser Error`, an empty grid and 0 items).
//
// Store-free: the page's connection and the grid's `conditions` (DrawingsPage's Gantt selection —
// trusted SQL fragments built by the page) come in as arguments. The columns, the default sort
// (`issueLastUpdated desc`) and the page-size menu are the package grid's; its header keys are the
// sort keys `fetchPage` maps below (issueClientId, issueType, issueDescription, responsibleParty,
// dueBy, issueLocationId, issueStatusId — `photo` is not sortable).
import { computed, ref, unref } from 'vue';
import { escapeSql } from '@rdrive/helper-sql';

/**
 * @param {object} deps
 * @param {object|import('vue').Ref} deps.conn  DuckDB connection (DuckDBService's gated `conn`)
 * @param {Array<string>|import('vue').Ref<Array<string>>} deps.conditions  extra WHERE fragments
 * @returns {{ fetchPage: Function, reloadKey: import('vue').ComputedRef<string>,
 *   reportWhereClause: import('vue').ComputedRef<string> }}
 */
export function useIssuesGridData({ conn, conditions }) {
    const conditionList = () => unref(conditions) || [];

    // The search of the latest page asked for — the report dialog's where-clause follows it
    const currentSearch = ref('');

    // Transform raw data into grouped items
    const transformItemsData = (rawItems) => {
        return rawItems.map(rawData => {
            return {
                id: rawData.id,
                clientId: rawData.issueClientId,
                templateId: rawData.issueTemplateId,
                templateTitle: rawData.templateTitle,
                locationId: rawData.issueLocationId,
                locationTitle: rawData.locationTitle,
                drawingId: rawData.drawingId,
                drawingTitle: rawData.drawingTitle,
                drawingUrl: rawData.drawingUrl,
                assigneeTeamName: rawData.assigneeTeamName,
                subcategoryId: rawData.subcategoryId || '',
                // New BI fields
                issueType: rawData.issueType,
                issueCategory: rawData.issueCategory,
                issueItem: rawData.issueItem,
                issueDescription: rawData.issueDescription,
                responsibleParty: rawData.responsibleParty,
                photo: rawData.photo,
                responsePhoto: rawData.responsePhoto,
                priority: rawData.priority,
                dueBy: rawData.dueBy,
                // Status info
                statusTitle: rawData.statusTitle,
                statusColor: rawData.statusColor,
                updatedDate: rawData.issueLastUpdated,
                isTerminal: rawData.IsTerminal,
                // For compatibility with existing code
                latestStatusTitle: rawData.statusTitle,
                latestStatusColor: rawData.statusColor,
                // Empty array for compatibility with hasSubItems function
                subItems: [],
                locationX: rawData.locationX,
                locationY: rawData.locationY
            };
        });
    };

    // Helper methods
    const getWhereClause = (search) => {
        const conditions = [];

        // Add conditions from props
        if (conditionList().length > 0) {
            conditions.push(...conditionList());
        }

        // Add search filter
        if (search?.trim()) {
            const searchTerm = escapeSql(search.trim().toLowerCase());
            const searchCondition = `(
            LOWER(i.issueClientId) LIKE '%${searchTerm}%'
            OR LOWER(i.issueType) LIKE '%${searchTerm}%'
            OR LOWER(i.issueCategory) LIKE '%${searchTerm}%'
            OR LOWER(i.issueItem) LIKE '%${searchTerm}%'
            OR LOWER(i.responsibleParty) LIKE '%${searchTerm}%'
            OR LOWER(i.priority) LIKE '%${searchTerm}%'
            OR LOWER(i.dueBy) LIKE '%${searchTerm}%'
            OR LOWER(i.issueDescription) LIKE '%${searchTerm}%'
            OR LOWER(it.title) LIKE '%${searchTerm}%'
            OR LOWER(t.name) LIKE '%${searchTerm}%'
            OR LOWER(l.title) LIKE '%${searchTerm}%'
            OR LOWER(ld.title) LIKE '%${searchTerm}%'
            OR LOWER(st.title) LIKE '%${searchTerm}%'
        )`;
            conditions.push(searchCondition);
        }

        return conditions.length > 0 ? `WHERE ${conditions.join(' AND ')}` : '';
    };

    // Helper method for report dialog (uses different table aliases)
    const getWhereClauseForReport = (search) => {
        const conditions = [];

        // Add conditions from props
        if (conditionList().length > 0) {
            conditions.push(...conditionList());
        }

        // Add search filter
        if (search?.trim()) {
            const searchTerm = escapeSql(search.trim().toLowerCase());
            const searchCondition = `(
            LOWER(i.issueClientId) LIKE '%${searchTerm}%'
            OR LOWER(i.issueType) LIKE '%${searchTerm}%'
            OR LOWER(i.issueCategory) LIKE '%${searchTerm}%'
            OR LOWER(i.issueItem) LIKE '%${searchTerm}%'
            OR LOWER(i.responsibleParty) LIKE '%${searchTerm}%'
            OR LOWER(i.priority) LIKE '%${searchTerm}%'
            OR LOWER(i.dueBy) LIKE '%${searchTerm}%'
            OR LOWER(i.issueDescription) LIKE '%${searchTerm}%'
            OR LOWER(it.title) LIKE '%${searchTerm}%'
            OR LOWER(t_assignee.name) LIKE '%${searchTerm}%'
            OR LOWER(l.title) LIKE '%${searchTerm}%'
            OR LOWER(ld.title) LIKE '%${searchTerm}%'
            OR LOWER(st.title) LIKE '%${searchTerm}%'
        )`;
            conditions.push(searchCondition);
        }

        return conditions.length > 0 ? `WHERE ${conditions.join(' AND ')}` : '';
    };

    // DuckDbDataTable's provider: one page of issues and the total. The statements run in the old
    // grid's order — the page's ids, the count, the page's rows.
    const fetchPage = async ({ page, itemsPerPage, sort, search }) => {
        // Update current search
        currentSearch.value = search || '';

        // Build WHERE clause
        const whereClause = getWhereClause(search);

        // Build ORDER BY clause
        let orderByColumn = 'i.id';
        let orderDirection = sort.order || 'desc';

        if (sort.key) {
            const columnMapping = {
                'issueTemplateId': 'it.title',
                'issueLocationId': 'l.title',
                'issueStatusId': 'st.title',
                'issueAssignee': 't.name',
                'issueClientId': 'i.issueClientId'
            };

            // Handle special case for BI fields with hyphens
            if (sort.key.startsWith('BI-')) {
                orderByColumn = `i."${sort.key}"`;
            } else {
                orderByColumn = columnMapping[sort.key] || `i.${sort.key}`;
            }
        }

        // Client IDs are formatted as "<CODE>-<NUMBER>" (e.g. CSN-10). Split on the
        // LAST hyphen: everything before it is the code prefix, everything after is
        // the number. Sort by prefix first, then numerically so CSN-2 < CSN-10.
        let orderByClause;
        if (sort.key === 'issueClientId') {
            const dir = orderDirection.toUpperCase();
            const prefixExpr = `regexp_replace(i.issueClientId, '-[^-]*$', '')`;
            const numberExpr = `TRY_CAST(regexp_extract(i.issueClientId, '-([^-]*)$', 1) AS BIGINT)`;
            orderByClause = `ORDER BY ${prefixExpr} ${dir}, ${numberExpr} ${dir}`;
        } else {
            orderByClause = `ORDER BY ${orderByColumn} ${orderDirection.toUpperCase()}`;
        }
        const limitClause = `LIMIT ${itemsPerPage}`;
        const offsetClause = `OFFSET ${(page - 1) * itemsPerPage}`;

        // Determine if joins are needed: for filters, conditions, or sorting by joined fields
        const joinRequiringSortKeys = ['issueTemplateId', 'issueLocationId', 'issueStatusId', 'issueAssignee'];
        const needsJoins = whereClause.length > 0
            || conditionList().length > 0
            || (sort.key && joinRequiringSortKeys.includes(sort.key));

        // Count query
        const countQuery = `
        SELECT COUNT(DISTINCT i.id) as total
        FROM filteredIssues i
        ${needsJoins ?
                `
            LEFT JOIN item_templates it ON i.issueTemplateId = it.id
            LEFT JOIN status_templates st ON i.issueStatusId = st.id
            LEFT JOIN teams t ON i.issueAssignee = t.id
            LEFT JOIN locations l ON i.issueLocationId = l.id
            LEFT JOIN drawings ld ON l.drawingId = ld.id
        ` : ''}
        ${whereClause}
    `;

        // Get item IDs for pagination
        const idsQuery = `
        SELECT DISTINCT i.id as id
        FROM filteredIssues i
        ${needsJoins ?
                `
            LEFT JOIN item_templates it ON i.issueTemplateId = it.id
            LEFT JOIN status_templates st ON i.issueStatusId = st.id
            LEFT JOIN teams t ON i.issueAssignee = t.id
            LEFT JOIN locations l ON i.issueLocationId = l.id
            LEFT JOIN drawings ld ON l.drawingId = ld.id
        ` : ''}
        ${whereClause}
        ${orderByClause}
        ${limitClause}
        ${offsetClause}
    `;

        const db = unref(conn);
        const idsResult = await db.query(idsQuery);
        const itemIds = idsResult.toArray().map(row => row.toJSON().id);

        // Execute count query
        const countResult = await db.query(countQuery);
        let totalItems = 0;
        if (countResult.numRows > 0) {
            totalItems = Number(countResult.toArray()[0].toJSON().total);
        }

        // No ids on this page: no rows (the old grid ran a `SELECT 1 WHERE 1=0` here)
        if (itemIds.length === 0) {
            return { items: [], totalItems };
        }

        // Items query (paginated)
        const itemsQuery = `
        SELECT
            i.*,
            it.title AS templateTitle,
            st.title AS statusTitle,
            st.color AS statusColor,
            st."IsTerminal" AS "IsTerminal",
            t.name AS assigneeTeamName,
            l.title AS locationTitle,
            l.drawingId AS drawingId,
            ld.title AS drawingTitle,
            ld.Url AS drawingUrl
        FROM filteredIssues i
        LEFT JOIN item_templates it ON i.issueTemplateId = it.id
        LEFT JOIN status_templates st ON i.issueStatusId = st.id
        LEFT JOIN teams t ON i.issueAssignee = t.id
        LEFT JOIN locations l ON i.issueLocationId = l.id
        LEFT JOIN drawings ld ON l.drawingId = ld.id
        WHERE i.id IN (${itemIds.map(id => `'${escapeSql(id)}'`).join(',')})
        ${orderByClause}
    `;

        const itemsResult = await db.query(itemsQuery);
        return {
            items: transformItemsData(itemsResult.toArray().map(row => row.toJSON())),
            totalItems
        };
    };

    // The grid's conditions as one value: a change reloads the table from page 1 (the old grid
    // compared them as JSON, so an equal new array does not reload)
    const reloadKey = computed(() => JSON.stringify(conditionList()));

    // The report dialog's where-clause: the grid's search and conditions over the report's aliases
    const reportWhereClause = computed(() => getWhereClauseForReport(currentSearch.value));

    return { fetchPage, reloadKey, reportWhereClause };
}
